import { FC, PropsWithChildren, useState } from 'react';
import styled from 'styled-components';

import { testIds } from '../../constants';
import { useSideBySideEditorContext } from '../SideBySideEditorContext';
import { HtmlLoaderContext } from './HtmlLoaderContext';

const HiddenInput = styled.input`
  display: none;
  height: 0;
  width: 0;
`;

type HtmlLoaderProviderProps = {
  canLoadHTML: boolean;
};

export const HtmlLoaderProvider: FC<PropsWithChildren<HtmlLoaderProviderProps>> = ({
  children,
  canLoadHTML,
}) => {
  const { editor } = useSideBySideEditorContext();
  const [fileUploadInput, setHtmlLoader] = useState<HTMLInputElement | null>(null);

  const handleUpload = () => {
    const htmlFile = fileUploadInput?.files?.[0];
    if (canLoadHTML && htmlFile) {
      const fileReader = new FileReader();

      fileReader.onload = () => {
        if (typeof fileReader.result === 'string') editor.current?.setValue(fileReader.result);
        // reset the file input
        // resolves edge case where the user may want to reload the same file more than once in succession.
        // (if the value is not cleared, the onChange event will not fire)
        if (fileUploadInput) fileUploadInput.value = '';
      };
      fileReader.readAsText(htmlFile);
    }
  };

  const showFilePicker = () => {
    if (canLoadHTML && fileUploadInput) {
      fileUploadInput.click();
    }
  };

  return (
    <HtmlLoaderContext.Provider value={{ canLoadHTML, showFilePicker }}>
      {children}
      {canLoadHTML && (
        <HiddenInput
          accept="text/plain, text/html"
          data-test={testIds.toolbarInputLoadHtml}
          multiple={false}
          onChange={handleUpload}
          ref={setHtmlLoader}
          type="file"
        />
      )}
    </HtmlLoaderContext.Provider>
  );
};
